/**
 * Signal Card Component
 */

import { el } from '../../shared/components/simpleRender.js';
import { createSparkline } from './SparklineChart.js';

const cardCharts = new Map();

const STATUS_CONFIG = {
  low: { label: 'LOW', color: 'var(--tg-fog-green)', bg: 'rgba(151, 184, 160, 0.15)' },
  normal: { label: 'NORMAL', color: 'var(--tg-fog-green)', bg: 'rgba(151, 184, 160, 0.15)' },
  elevated: { label: 'ELEVATED', color: '#f97316', bg: 'rgba(249, 115, 22, 0.15)' },
  high: { label: 'HIGH', color: 'var(--tg-rust-red)', bg: 'rgba(178, 51, 42, 0.15)' },
  extreme: { label: 'EXTREME', color: 'var(--tg-flash-crash)', bg: 'rgba(255, 0, 51, 0.15)' },
};

const TIER_ICONS = { free: '🔓', pro: '🔐', enterprise: '💎' };

const CATEGORY_COLORS = {
  liquidity: 'var(--tg-liquidity-cyan)',
  credit: 'var(--tg-rust-red)',
  rates: 'var(--tg-lantern-gold)',
  funding: 'var(--tg-fog-green)',
  volatility: '#a855f7',
};

function formatValue(value, signalId) {
  if (value == null) return '—';
  if (signalId === 'NET_LIQUIDITY') return `$${(value / 1e6).toFixed(2)}T`;
  if (signalId === 'LIQUIDITY_REGIME') return String(value).toUpperCase();
  if (Math.abs(value) >= 1000) return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (Math.abs(value) < 0.01) return value.toFixed(4);
  return value.toFixed(2);
}

function formatChange(change) {
  if (!change) return null;
  const arrow = change.direction === 'up' ? '▲' : change.direction === 'down' ? '▼' : '—';
  const pct = change.percent != null ? `${change.percent >= 0 ? '+' : ''}${change.percent.toFixed(1)}%` : '';
  return { arrow, pct, direction: change.direction };
}

/**
 * Converts API history data to chart format
 */
function toChartData(historyData) {
  if (!historyData || !Array.isArray(historyData)) return [];
  
  return historyData
    .filter(d => d.value != null)
    .map(d => {
      let timestamp;
      if (typeof d.time === 'number') {
        timestamp = d.time;
      } else if (typeof d.date === 'number') {
        timestamp = d.date;
      } else if (typeof d.date === 'string') {
        timestamp = Math.floor(new Date(d.date).getTime() / 1000);
      } else if (typeof d.time === 'string') {
        timestamp = Math.floor(new Date(d.time).getTime() / 1000);
      } else {
        return null;
      }
      return { time: timestamp, value: d.value };
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time);
}

export function createSignalCard(signal, historyData = [], onClick = null) {
  const status = signal.current?.level || 'normal';
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.normal;
  const catColor = CATEGORY_COLORS[signal.category] || 'var(--tg-fog-green)';
  const change = formatChange(signal.change);

  const card = el('div', {
    className: `signal-card signal-card--${status}`,
    'data-signal-id': signal.signal_id,
    onclick: onClick ? () => onClick(signal) : null,
    style: onClick ? 'cursor: pointer;' : '',
  }, [
    el('div', { className: 'signal-card__header' }, [
      el('span', { className: 'signal-card__id' }, [signal.signal_id]),
      el('div', { className: 'signal-card__badges' }, [
        el('span', {
          className: 'signal-card__status',
          style: `color: ${statusCfg.color}; background: ${statusCfg.bg}; border-color: ${statusCfg.color}`
        }, [statusCfg.label]),
      ]),
    ]),
    el('div', { className: 'signal-card__value-row' }, [
      el('span', { className: 'signal-card__value' }, [formatValue(signal.current?.value, signal.signal_id)]),
      change ? el('span', {
        className: `signal-card__change change-${change.direction}`
      }, [`${change.arrow} ${change.pct}`]) : null,
    ].filter(Boolean)),
    el('div', { className: 'signal-card__chart', 'data-chart-container': 'true' }, []),
    el('div', { className: 'signal-card__footer' }, [
      el('span', {}, [`Updated: ${signal.current?.date || '—'}`]),
      el('span', {}, [`${signal.frequency || 'D'} • ${signal.inputs?.length || 0} inputs`]),
    ]),
  ].filter(Boolean));

  // Initialize chart after DOM insertion
  requestAnimationFrame(() => {
    const chartContainer = card.querySelector('[data-chart-container]');
    if (chartContainer) {
      const chartData = toChartData(historyData);
      console.log(`[Card ${signal.signal_id}] Chart data points:`, chartData.length);
      
      if (chartData.length > 0) {
        const chartInstance = createSparkline(chartContainer, chartData, { status });
        cardCharts.set(signal.signal_id, chartInstance);
      } else {
        chartContainer.innerHTML = '<div style="color: var(--tg-text-muted); font-size: 11px; text-align: center; padding: 20px 0;">No chart data</div>';
      }
    }
  });

  return card;
}

export function createSignalCardPlaceholder() {
  return el('div', { className: 'signal-card signal-card--loading' }, [
    el('div', { className: 'signal-card__header' }, [
      el('div', { className: 'signal-card__skeleton signal-card__skeleton--sm' }, []),
      el('div', { className: 'signal-card__skeleton signal-card__skeleton--xs' }, []),
    ]),
    el('div', { className: 'signal-card__skeleton signal-card__skeleton--title' }, []),
    el('div', { className: 'signal-card__skeleton signal-card__skeleton--value' }, []),
    el('div', { className: 'signal-card__skeleton signal-card__skeleton--chart' }, []),
    el('div', { className: 'signal-card__skeleton signal-card__skeleton--text' }, []),
  ]);
}

export function createSignalCardError(signalId, message, onRetry) {
  return el('div', { className: 'signal-card signal-card--error' }, [
    el('div', { className: 'signal-card__error-content' }, [
      el('div', { className: 'signal-card__error-icon' }, ['⚠']),
      el('p', { className: 'signal-card__error-message' }, [message || `Failed to load ${signalId}`]),
      onRetry ? el('button', { className: 'signal-card__retry-btn', onclick: onRetry }, ['Retry']) : null,
    ].filter(Boolean)),
  ]);
}

export function destroySignalCard(signalId) {
  const chartInstance = cardCharts.get(signalId);
  if (chartInstance?.destroy) {
    chartInstance.destroy();
    cardCharts.delete(signalId);
  }
}
